import { motion } from "framer-motion";
import { ShieldCheck, Plane, Database, Check } from "lucide-react";
import Reveal from "./Reveal";

const PROJECTS = [
  {
    tag: "01",
    icon: ShieldCheck,
    title: "Dedicated Safety Management System Platform",
    desc: "The newly designed digital Safety Management System (SMS) platform transitions 90% of our client's manual paperwork into an automated, web based workflow that directly aligns with their Standard Operations Manual. By replacing fragmented spreadsheets and administrative hurdles, this centralised system equips the business with real time risk visibility, guaranteed regulatory compliance, and a proactive, data driven workplace health and safety (WHS) culture.",
    features: [
      {
        t: "Incident & Hazard Reporting",
        d: "Streamlines user friendly logging of near misses, injuries, and hazards directly from the field.",
      },
      {
        t: "Automated Compliance Tracking",
        d: "Monitors staff records, mandatory training intervals, and audit deadlines automatically.",
      },
      {
        t: "Corrective and Preventive Actions (CAPA)",
        d: "Assigns, tracks, and escalates safety actions to ensure timely resolution of systemic vulnerabilities.",
      },
      {
        t: "Audit & Inspection Management",
        d: "Schedules and executes digital safety audits using customisable checklists with offline mobile capability.",
      },
      {
        t: "Safety Analytics Dashboards",
        d: "Aggregates safety data into live executive summaries to pinpoint incident trends and lead indicators.",
      },
      {
        t: "Contractor Safety Verification",
        d: "Validates third party inductions, licenses, and insurance documentation before site access is granted.",
      },
    ],
  },
  {
    tag: "02",
    icon: Plane,
    title: "Agri-Aviation Operational Management Platform",
    desc: "We have successfully modernised a legacy MS Access application into a secure, enterprise grade web based system. This upgrade eliminates past operational bottlenecks and provides a scalable foundation for our client's growing business needs.",
    features: [
      {
        t: "True Multiuser Collaboration",
        d: "Enables global, simultaneous data entry with zero system lag or file locking.",
      },
      {
        t: "Seamless Remote Access",
        d: "Cloud architecture allows your team to securely connect from any device or location.",
      },
      {
        t: "Unified Line-of-Business Operations",
        d: "Consolidates separate software features into one centralized platform.",
      },
      {
        t: "Automated Document Recency",
        d: "Live monitoring tracks compliance and ensures critical documents stay up to date.",
      },
      {
        t: "Native Mission Tracking",
        d: "Supports complex air bombing and variable rate application logging without manual workarounds.",
      },
    ],
  },
  {
    tag: "03",
    icon: Database,
    title: "Unified SME Operations Database: Laundromat & Fleet Management",
    desc: "The Integrated SME Database served as a unified operational system seamlessly bridging the automation of the laundromat and moped rental operation. For the laundromat, it delivers machine level revenue tracking paired with a QR code driven machine breakdown customer reporting. Simultaneously, the system powers the moped rental operation by capturing secure customer profiles, automating digital rental agreements, and hosting real time vehicle condition image uploads, centralising all inventory, fee tracking, and financial documentation into a single, high efficiency dashboard.",
    features: [],
  },
];

export default function Projects() {
  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="relative py-24 md:py-32 lg:py-40 border-t border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal className="max-w-3xl mb-16 md:mb-20">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-px w-12 bg-[#00F0FF]" />
            <span className="text-sm uppercase tracking-[0.25em] text-[#00F0FF]">
              Recent Projects
            </span>
          </div>
          <h2 className="font-display font-bold tracking-tighter text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            Our recent implemented projects.
          </h2>
        </Reveal>

        <div className="space-y-8 lg:space-y-10">
          {PROJECTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.tag} delay={i * 0.08}>
                <motion.article
                  data-testid={`project-card-${i}`}
                  whileHover={{ y: -4 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  className="group rounded-3xl border border-white/10 bg-[#0C1322] p-7 md:p-10 lg:p-12 transition-colors duration-300 hover:border-[#00F0FF]/40"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    {/* Left: heading + description */}
                    <div className="lg:col-span-5">
                      <div className="flex items-center gap-4">
                        <span className="grid place-items-center h-14 w-14 rounded-xl bg-white/5 border border-white/10 text-[#00F0FF] transition-colors duration-300 group-hover:bg-[#00F0FF] group-hover:text-[#050A15]">
                          <Icon size={26} />
                        </span>
                        <span className="font-display text-5xl md:text-6xl font-black text-white/5 leading-none">
                          {p.tag}
                        </span>
                      </div>
                      <h3 className="mt-7 font-display text-2xl md:text-3xl font-bold text-white leading-tight">
                        {p.title}
                      </h3>
                      <p className="mt-5 text-[#94A3B8] font-light leading-relaxed">
                        {p.desc}
                      </p>
                    </div>

                    {/* Right: features */}
                    <div className="lg:col-span-7">
                      {p.features.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                          {p.features.map((f) => (
                            <div key={f.t} className="flex gap-3">
                              <span className="mt-1 grid place-items-center h-6 w-6 shrink-0 rounded-md bg-[#00F0FF]/10 text-[#00F0FF]">
                                <Check size={14} />
                              </span>
                              <div>
                                <div className="font-display font-semibold text-white text-[15px] leading-snug">
                                  {f.t}
                                </div>
                                <div className="mt-1.5 text-sm text-[#94A3B8] font-light leading-relaxed">
                                  {f.d}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="h-full flex items-center">
                          <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-8 w-full">
                            <p className="font-display text-lg text-white/90 leading-relaxed">
                              A single, high efficiency dashboard unifying two
                              distinct business operations, laundromat revenue
                              tracking and moped fleet rental management.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-2.5">
                              {[
                                "Machine-level revenue",
                                "QR breakdown reporting",
                                "Digital rental agreements",
                                "Vehicle condition uploads",
                                "Unified financial records",
                              ].map((chip) => (
                                <span
                                  key={chip}
                                  className="rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs text-[#94A3B8]"
                                >
                                  {chip}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
